import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';

import { BasedataService } from '../shared/basedata.service';

import { PhaseData, Project, Projects } from './project';
import { ConfigService } from '@docma-mm-core/config/config.service';
import { ProjectDataService } from './project.data.service';

const savedProjectsKey = 'mm-savedProjects';
const selectedProjectKey = 'mm-selectedProject';
const savedPhasesKey = 'mm-savedPhases';

@Injectable()
export class ProjectHttpService extends BasedataService implements ProjectDataService {

  private phases: PhaseData[] = [];

  private projectUrl = '/projects';

  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getProjectUrl() {
    return super.getFullUrl(this.projectUrl);
  }

  // get projects
  getProjects(top: number = 10000): Observable<Projects> {
    const endpoint = this.getProjectUrl();
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    return this.http
      .get<Projects>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // search projects
  searchProjects(top: number = 10000,
                 skip: number = 0,
                 id: number = null,
                 name1: string = null,
                 name2: string = null,
                 uid: string = null,
                 phases: number = null,
                 projectStart: string = null,
                 projectEnd: string = null): Observable<Projects> {
    const endpoint = this.getProjectUrl() + '/search';
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    if (id) {
      httpParams = httpParams.set('id', id.toString());
    }
    if (name1) {
      httpParams = httpParams.set('name1', name1);
    }
    if (name2) {
      httpParams = httpParams.set('name2', name2);
    }
    if (uid) {
      httpParams = httpParams.set('uid', uid);
    }
    if (phases) {
      httpParams = httpParams.set('phases', phases.toString());
    }
    if (projectStart) {
      httpParams = httpParams.set('projectStart', projectStart);
    }
    if (projectEnd) {
      httpParams = httpParams.set('projectEnd', projectEnd);
    }
    return this.http
      .get<Projects>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get projects by phases
  getProjectsByPhases(phase: number = 1): Observable<Projects> {
    const endpoint = this.getProjectUrl();
    let httpParams = new HttpParams();
    if (phase) {
      httpParams = httpParams.set('phase', phase.toString());
    }
    return this.http
      .get<Projects>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get project by id
  getProjectById(id: number): Observable<Project> {
    const endpoint = this.getProjectUrl() + '/' + id;
    return this.http
      .get<Project>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get project by mzdocma_id
  getProjectByMydocmaId(muid: string): Observable<Project> {
    const endpoint = this.getProjectUrl() + '/?muid=' + muid;
    return this.http
      .get<Project>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get project by uid
  getProjectByUid(uid: string): Observable<Project> {
    const endpoint = this.getProjectUrl() + '/' + uid;
    return this.http
      .get<Project>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create project
  createProject(project: Project): Observable<Project> {
    return this.http
      .post<Project>(this.getProjectUrl(), JSON.stringify(project), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // update existing project
  updateProject(project: Project): Observable<Project> {
    return this.http
      .put<Project>(this.getProjectUrl() + `/${project.Id}`, JSON.stringify(project), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }
}
